'use client'

import { ChangeEvent, MouseEvent, ReactNode, RefObject, useRef } from 'react'
import { Button, Cell, Text, Card, Image, Icon, Spacer } from '@vinted/web-ui'
import { useFormContext } from 'react-hook-form'
import { X16 } from '@vinted/monochrome-icons'
import { noop } from 'lodash'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { FormDocumentFileModel } from '@marketplace-web/pay-identity/payments-identity-data'

import { Field } from '../../types'

type Props = {
  name: string
  index?: number
  acceptedFileTypes?: string
  title?: string
  value?: File | null
  validation?: ReactNode
  disabled?: boolean
  styling?: React.ComponentProps<typeof Cell>['styling']
  translationPrefix?: string
  isInfoLinkVisible: boolean
  maximumFileSize: number
  fileInputRef?: RefObject<HTMLInputElement | null>
  inputInformationLink?: {
    information_link: (chunks: Array<ReactNode>) => JSX.Element
  }
  onChange?: (file: File | null, message?: string) => void
  onButtonClick?: ((currentButtonState: 'upload' | 'delete') => void) | null
}

const FileInput = ({
  name,
  index,
  acceptedFileTypes,
  value,
  title,
  validation,
  styling,
  disabled,
  translationPrefix,
  isInfoLinkVisible,
  maximumFileSize,
  fileInputRef,
  inputInformationLink,
  onChange = noop,
  onButtonClick = null,
}: Props) => {
  const baseTranslate = useTranslate()
  const translate = useTranslate(translationPrefix)

  const mainFileInputRef = useRef<HTMLInputElement>(null)
  const fileInput = fileInputRef || mainFileInputRef

  const { watch } = useFormContext()

  const supportingDocuments = watch(Field.SupportingDocuments)

  function onFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files?.length) return

    const file = event.target.files[0]!

    if (file.size > maximumFileSize) {
      const maxFileSizeInMegabytes = maximumFileSize / 1024 / 1024

      onChange(file, baseTranslate('errors.max_file_size', { size: maxFileSizeInMegabytes }))

      return
    }

    onChange(file)
  }

  function onFileInputClick(event: MouseEvent) {
    const element = event.target as HTMLInputElement

    element.value = ''
  }

  function handleClick() {
    if (onButtonClick) {
      onButtonClick(value ? 'delete' : 'upload')

      return
    }

    if (value) {
      onChange(null)
    } else {
      fileInput.current?.click()
    }
  }

  function renderTitle() {
    return (
      <div className="u-flexbox u-flex-direction-column">
        <span>{title || translate('title')}</span>
        {isInfoLinkVisible && !!inputInformationLink && !value && (
          <Text
            as="h3"
            html
            type={Text.Type.Subtitle}
            text={translate('what_it_is', inputInformationLink)}
          />
        )}
      </div>
    )
  }

  const renderPhoto = (file: FormDocumentFileModel) => {
    const src = `data:${file.mediaType};base64,${file.body}`

    return (
      <>
        <Image
          src={src}
          size={Image.Size.X4Large}
          styling={Image.Styling.Rounded}
          alt={baseTranslate('kyc_form.a11y.identity_document.uploaded_document')}
          testId="supporting-document__image-preview"
        />
        <div className="supporting-document__document-delete-button">
          <Button
            testId={`delete-button__${title}`}
            icon={
              <Icon
                name={X16}
                color={Icon.Color.GreyscaleLevel1}
                aria={{ 'aria-label': translate('buttons.delete') }}
              />
            }
            size={Button.Size.Small}
            inverse
            styling={Button.Styling.Filled}
            inline
            onClick={handleClick}
          />
        </div>
      </>
    )
  }

  const renderFile = (file: FormDocumentFileModel) => {
    if (!file) return null

    return (
      <div key={index} className="u-ui-margin-right-large u-ui-margin-bottom-large">
        <Spacer size={Spacer.Size.XLarge} />
        <Card>
          <div className="supporting-document__uploader-file-content u-overflow-hidden">
            {file.body && renderPhoto(file)}
          </div>
        </Card>
      </div>
    )
  }

  const renderBody = () => {
    if (index === undefined || !value) return null

    return (
      <div className="supporting-document__uploader-container u-flex-wrap">
        {supportingDocuments[index]?.files?.map(renderFile)}
      </div>
    )
  }

  return (
    <div className="file-input">
      <Cell
        styling={styling}
        disabled={disabled}
        title={renderTitle()}
        suffix={
          !value && (
            <Button
              text={translate('buttons.upload')}
              size={Button.Size.Small}
              theme="primary"
              onClick={handleClick}
              testId={`upload-button__${title}`}
            />
          )
        }
        body={renderBody()}
        validation={validation}
      />
      <input
        name={name}
        ref={fileInput}
        onChange={onFileInputChange}
        onClick={onFileInputClick}
        hidden
        type="file"
        accept={acceptedFileTypes}
        data-testid="file-input"
      />
    </div>
  )
}

export default FileInput
